import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { ArrowLeft, CreditCard, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-toastify';
import PRICING from '../config/pricing';

// Load Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const PaymentForm = ({ jobId, onPaymentSuccess, job, clientSecret }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isLoading, setIsLoading] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [currency, setCurrency] = useState('usd');

  useEffect(() => {
    if (clientSecret && job) {
      // Use the actual payment amount from the job (already calculated with free trial consideration)
      const actualAmount = job.payment?.amount || 0;
      setPaymentAmount(actualAmount);
      setCurrency(job.payment?.currency || 'usd');
    }
  }, [clientSecret, job]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsLoading(true);

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
      });

      if (error) {
        toast.error(error.message);
        console.error('Payment error:', error);
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        // Verify payment on backend
        const verifyResponse = await axios.post('/api/payments/verify-payment', {
          jobId,
          paymentIntentId: paymentIntent.id
        }, { withCredentials: true });

        if (verifyResponse.data.success) {
          toast.success('Payment successful! Starting processing...');
          onPaymentSuccess();
        } else {
          toast.error('Payment verification failed');
        }
      }
    } catch (error) {
      toast.error('Payment processing failed');
      console.error('Payment processing error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // PaymentForm will be rendered only when clientSecret is available

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center">
          <CreditCard className="h-5 w-5 text-blue-600 mr-2" />
          <div>
            <h3 className="text-sm font-medium text-blue-800">
              Processing Fee: ${(paymentAmount / 100).toFixed(2)} USD
            </h3>
            <p className="text-sm text-blue-600">
              {job?.payment?.freePhotosUsed > 0 ? (
                `${job.payment.freePhotosUsed} free + ${job.progress?.totalPhotos - job.payment.freePhotosUsed} × ${PRICING.getDisplayRate()}`
              ) : (
                `${job?.progress?.totalPhotos} photos × ${PRICING.getDisplayRate()} per photo`
              )}
              {paymentAmount === 100 && job?.progress?.totalPhotos < 10 && ` (minimum ${PRICING.getMinimumDisplay()})`}
            </p>
          </div>
        </div>
      </div>
      
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <PaymentElement />
      </div>
      
      <button
        type="submit"
        disabled={!stripe || isLoading}
        className="w-full bg-green-600 text-white py-3 px-4 rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center justify-center"
      >
        {isLoading ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
            Processing payment...
          </>
        ) : (
          <>
            <CreditCard className="h-4 w-4 mr-2" />
            Pay ${(paymentAmount / 100).toFixed(2)} & Start Processing
          </>
        )}
      </button>
    </form>
  );
};

const Payment = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clientSecret, setClientSecret] = useState('');

  useEffect(() => {
    const fetchJob = async () => {
      try {
        const response = await axios.get(`/api/photos/job/${jobId}`, { 
          withCredentials: true 
        });
        const jobData = response.data;
        
        setJob(jobData);
        
        // If job doesn't require payment, redirect to job results
        if (!jobData.requiresPayment && jobData.status !== 'payment_pending') {
          navigate(`/job/${jobId}`);
          return;
        }
        
      } catch (error) {
        toast.error('Failed to load job details');
        navigate('/');
      } finally {
        setLoading(false);
      }
    };

    if (jobId) {
      fetchJob();
    }
  }, [jobId, navigate]);

  const handlePaymentSuccess = () => {
    navigate(`/job/${jobId}`);
  };

  useEffect(() => {
    const createPaymentIntent = async () => {
      if (!job) return;
      
      try {
        const response = await axios.post('/api/payments/create-payment-intent', 
          { jobId }, 
          { withCredentials: true }
        );
        
        if (response.data.status === 'already_paid') {
          toast.success(response.data.message);
          handlePaymentSuccess();
          return;
        }
        
        setClientSecret(response.data.clientSecret);
      } catch (error) {
        toast.error('Failed to initialize payment');
        console.error('Payment initialization error:', error);
      }
    };

    if (job && jobId) {
      createPaymentIntent();
    }
  }, [job, jobId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading payment details...</p>
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Job Not Found</h2>
          <p className="text-gray-600 mb-4">The requested job could not be found.</p>
          <Link to="/" className="text-blue-600 hover:text-blue-800">
            Return to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/photos" className="inline-flex items-center text-blue-600 hover:text-blue-800 mb-4">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Photo Selection
          </Link>
          
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Complete Your Payment
          </h1>
          <p className="text-gray-600">
            Pay to start AI metadata generation for your {job.progress?.totalPhotos} selected photos
          </p>
        </div>

        {/* Job Summary */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Order Summary</h3>
          
          <div className="space-y-3">
            {/* Explicit processing fee line to surface amount even if Stripe isn't initialized */}
            <p className="text-sm text-gray-700">
              {`Processing Fee: $${((job.payment?.amount || 0) / 100).toFixed(2)} USD`}
            </p>
            
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Photos selected:</span>
              <span className="font-medium">{job.progress?.totalPhotos}</span>
            </div>
            
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Style:</span>
              <span className="font-medium capitalize">{job.config?.style}</span>
            </div>
            
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Description sentences:</span>
              <span className="font-medium">{job.config?.descriptionSentences}</span>
            </div>
            
            {/* Free trial information */}
            {job.payment?.freePhotosUsed > 0 && (
              <div className="bg-green-50 border border-green-200 rounded-md p-3">
                <div className="flex items-center text-sm">
                  <CheckCircle className="h-4 w-4 text-green-600 mr-2 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-green-800 font-medium">
                      {job.payment.freePhotosUsed} free photos included
                    </p>
                    <p className="text-green-600 text-xs">
                      You're only paying for {job.progress?.totalPhotos - job.payment.freePhotosUsed} photos
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            <div className="border-t pt-3">
              <div className="flex justify-between">
                <span className="font-medium text-gray-900">Total:</span>
                <span className="font-bold text-gray-900">
                  {PRICING.formatPrice(job.payment?.amount / 100)} USD
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {job.payment?.freePhotosUsed > 0 ? (
                  `${job.payment.freePhotosUsed} free + ${job.progress?.totalPhotos - job.payment.freePhotosUsed} × ${PRICING.getDisplayRate()}`
                ) : (
                  `${PRICING.getDisplayRate()} per photo, minimum ${PRICING.getMinimumDisplay()}`
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Payment Form */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Payment Details</h3>
          
          {(() => {
            if (!clientSecret) {
              return (
                <div className="flex justify-center items-center h-32">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                  <span className="ml-2 text-gray-600">Initializing payment...</span>
                </div>
              );
            }

            // Only render Stripe Elements when the client secret looks valid to avoid dev overlay errors in tests
            const isValidClientSecret = /^pi_[^_]+_secret_[^_]+$/.test(clientSecret);
            if (stripePromise && isValidClientSecret) {
              return (
                <Elements 
                  stripe={stripePromise}
                  options={{
                    clientSecret,
                    appearance: {
                      theme: 'stripe',
                      variables: { colorPrimary: '#10b981' }
                    }
                  }}
                >
                  <PaymentForm 
                    jobId={jobId} 
                    onPaymentSuccess={handlePaymentSuccess}
                    job={job}
                    clientSecret={clientSecret}
                  />
                </Elements>
              );
            }

            // Fallback panel when Stripe isn't initialized (e.g., in tests without a real client secret)
            return (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
                Payment form is unavailable in this environment. You can still review the order summary above.
              </div>
            );
          })()}
        </div>

        {/* Security Notice */}
        <div className="mt-6 text-center">
          <div className="flex items-center justify-center text-sm text-gray-500">
            <CheckCircle className="h-4 w-4 mr-1" />
            Secure payment powered by Stripe
          </div>
        </div>
      </div>
    </div>
  );
};

export default Payment;
